import { parseScenarioFile } from '@orgsim/shared';
import { type FormEvent, useState } from 'react';
import { OfflineError } from '../api/client';
import { useSession } from '../api/session';
import { useAppStore } from '../store/appStore';
import { useRunStore } from '../store/runStore';
import { Icon } from './Icon';
import { pickTextFile } from './download';

export function LoginScreen() {
  const state = useSession((s) => s.state);
  const login = useSession((s) => s.login);
  const refresh = useSession((s) => s.refresh);
  const openRun = useAppStore((s) => s.openRun);
  const startRun = useRunStore((s) => s.start);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(username, password);
    } catch (err) {
      // An unreachable server switches to the offline view, which says so itself.
      if (!(err instanceof OfflineError))
        setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  const reconnect = async () => {
    setError(null);
    await refresh();
  };

  const importOffline = async () => {
    const text = await pickTextFile();
    if (!text) return;
    try {
      const scenario = parseScenarioFile(text);
      await startRun(scenario);
      openRun();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 p-8">
      <div className="brand">
        <Icon name="map" size={23} />
        <span>OrgSim</span>
      </div>
      <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-6">
        {state === 'offline' ? (
          <div className="flex flex-col gap-4">
            <p className="notice warning" role="alert">
              Der Server ist nicht erreichbar. Ohne Anmeldung lässt sich nur eine zuvor exportierte
              Szenariodatei ausführen.
            </p>
            {error ? (
              <p className="notice warning" role="alert">
                {error}
              </p>
            ) : null}
            <button type="button" className="button primary" onClick={importOffline}>
              <Icon name="upload" size={16} />
              Szenariodatei importieren
            </button>
            <button type="button" className="button" onClick={reconnect}>
              Erneut verbinden
            </button>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={submit}>
            <label className="flex flex-col gap-1 text-sm" htmlFor="login-username">
              <span>Benutzername</span>
              <input
                id="login-username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm" htmlFor="login-password">
              <span>Passwort</span>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            {error ? (
              <p className="notice warning" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="button primary" disabled={pending}>
              Anmelden
            </button>
            <p className="muted">Zugänge vergibt ein Administrator.</p>
          </form>
        )}
      </div>
    </div>
  );
}
